import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import * as Yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import MenuList from '@mui/material/MenuList'
import Stack from '@mui/material/Stack'
import { DesktopDateRangePicker, StaticDateRangePicker } from '@mui/x-date-pickers-pro'
import DialogTitle from '@components/_mui/DialogTitle'

import { getTestId, isValidDayjs } from '@shared/utils'

import DateRangeShortcuts from './DateRangeShortcuts'

DateRangeDialog.propTypes = {
  /** If `true`, the dialog is open */
  open: PropTypes.bool.isRequired,

  /** Callback fired when the dialog is closed */
  onClose: PropTypes.func.isRequired,

  /** Initial Dayjs values of range */
  initialValue: PropTypes.array,

  /** Callback fired when the value changes */
  onChange: PropTypes.func.isRequired,

  /** Longest allowed range in years */
  maxYears: PropTypes.number,
}

/**
 * Date range dialog
 *
 * @example
 * <DateRangeDialog
 *    open={open}
 *    onClose={() => setOpen(false)}
 *    initialValue={value}
 *    onChange={setValue}
 *  />
 */
export default function DateRangeDialog({ open, onClose, initialValue, onChange, maxYears, ...rest }) {
  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <Content onClose={onClose} initialValue={initialValue} onChange={onChange} maxYears={maxYears} {...rest} />
    </Dialog>
  )
}

// Separate state from dialog, so it resets when dialog is closed
function Content({ onClose, initialValue = [], onChange, maxYears, ...rest }) {
  const testId = getTestId(rest, 'date-range-dialog')

  const formik = useFormik({
    initialValues: {
      startDate: initialValue[0] || null,
      endDate: initialValue[1] || null,
    },
    validationSchema: getValidationSchema(maxYears),
    onSubmit: ({ startDate, endDate }) => {
      onChange([startDate, endDate])
      onClose()
    },
  })

  const rangeError = Boolean(formik.errors.range)

  const startError = formik.touched.startDate && formik.errors.startDate
  const startFieldError = Boolean(startError) || rangeError

  const endError = formik.touched.endDate && formik.errors.endDate
  const endFieldError = Boolean(endError) || rangeError

  const value = [formik.values.startDate, formik.values.endDate]
  const handleChange = ([startDate, endDate]) => {
    formik.setFieldValue('startDate', startDate)
    formik.setFieldValue('endDate', endDate)
  }

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <DialogTitle>Select date range</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <MenuList>
              <DateRangeShortcuts value={value} onChange={handleChange} data-testid={`${testId}-shortcuts`} />
            </MenuList>
          </Grid>
          <Grid size={{ xs: 12, sm: 9 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <StaticDateRangePicker
                displayStaticWrapperAs="desktop"
                value={value}
                onChange={handleChange}
                slotProps={{ actionBar: { actions: [] } }}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            <Stack>
              <DesktopDateRangePicker
                disableOpenPicker
                value={value}
                onChange={handleChange}
                slotProps={{
                  textField: ({ position }) => {
                    const name = position === 'start' ? 'startDate' : 'endDate'
                    return {
                      fullWidth: true,
                      id: name,
                      name,
                      error: position === 'start' ? startFieldError : endFieldError,
                      helperText: position === 'start' ? startError : endError,
                      slotProps: {
                        inputLabel: { shrink: true },
                        htmlInput: { 'data-testid': `${testId}-${name}` },
                      },
                    }
                  },
                }}
              />
              {formik.errors.range && <FormHelperText error>{formik.errors.range}</FormHelperText>}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" data-testid={`${testId}-submit`}>
          Ok
        </Button>
      </DialogActions>
    </form>
  )
}

const getValidationSchema = (maxYears) =>
  Yup.object()
    .shape({
      startDate: Yup.object().nullable().required('Required').test('invalid-start', 'Invalid Date', isValidDayjs),
      endDate: Yup.object().nullable().required('Required').test('invalid-end', 'Invalid Date', isValidDayjs),
    })
    .test({
      name: 'validate-range',
      test: function ({ startDate, endDate }) {
        // Let the field-level errors handle "Invalid Date"
        if (!isValidDayjs(startDate) || !isValidDayjs(endDate)) return false

        if (startDate.isAfter(endDate)) {
          return this.createError({ path: 'range', message: 'Start must be before End' })
        }

        if (maxYears && endDate.diff(startDate, 'year') >= maxYears) {
          return this.createError({ path: 'range', message: `Range cannot exceed ${maxYears} ${maxYears === 1 ? 'year' : 'years'}` })
        }

        return true
      },
    })
